/* eslint-disable no-undef */
import { tabletMax } from '@propertypal/shared/src/constants/mediaQueries';
import { Property, Agent, Broker } from '@propertypal/shared/src/types/property';
import { EnquiryContactDetails } from '@propertypal/shared/src/utils/enquiries/getContact';
import platform from '@propertypal/shared/src/utils/platform';
import { getAgentColours } from '@propertypal/shared/src/utils/property/getSellingAgent';
import Link from 'next/link';
import router from 'next/router';
import React, { CSSProperties, FunctionComponent, useEffect, useRef, useState } from 'react';
import Button from '../buttons/Button';
import WhiteButton from '../buttons/WhiteButton';
import { RowAlignCenter } from '../layout';
import useMediaQuery from '../media-query/useMediaQuery';
import {
  AgentText,
  AgentBoxFixed,
  AgentBoxFixedButtons,
  LicenceWarning,
  NameAndPSRTextWrapper,
} from './PropertyAgentBoxFixed.style';
import PropertyPhoneNumberButton from './PropertyPhoneNumberButton';

interface Props {
  containerStyle?: CSSProperties;
  property: Property;
  onCallClick: (location: string) => void;
  onShowNumberClick: (location: string, accountNumber?: string) => void;
  enquireNowClick: (location: string, accountNumber?: string) => void;
  registerInterestClick: (location: string) => void;
  registerInterest?: boolean;
  leadId: number;
  sellingAgent: Agent | undefined;
  contactInfo: EnquiryContactDetails;
  leadsLink: string;
  broker?: Broker | null;
}

const getTotalButtons = (props: Props, contactInfo: EnquiryContactDetails) => {
  let total = 0;

  if (contactInfo.email) total += 1;
  if (contactInfo.phone) total += 1;
  if (props.broker && platform.type === 'propertypal') total += 1;
  if (props.registerInterest) total += 1;

  return total;
};

const PropertyAgentBoxFixed: FunctionComponent<Props> = (props) => {
  const { pdpBrandingButtonBgColour, pdpBrandingButtonTextColour } = getAgentColours(
    props.property.enhancedBranding,
    props.sellingAgent,
  );
  const contactBox = useRef<HTMLDivElement | null>(null);
  const [showEnquiryBar, setShowEnquiryBar] = useState(false);
  const totalButtons = getTotalButtons(props, props.contactInfo);
  const isMobile = useMediaQuery({ query: `(${tabletMax})` });
  const useShortText = isMobile && totalButtons >= 4;

  useEffect(() => {
    const checkBelowThreshold: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        setShowEnquiryBar(entry.boundingClientRect.top < 0);
      });
    };

    const observer = new IntersectionObserver(checkBelowThreshold, {
      threshold: 1,
    });

    if (contactBox.current) {
      observer.observe(contactBox.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <AgentBoxFixed
        className="hide-print"
        data-testid="agent-box-fixed"
        style={{ top: showEnquiryBar ? 0 : -60, visibility: showEnquiryBar ? 'visible' : 'hidden' }}
      >
        {props.property.developerAsPrimaryBrand && (
          <RowAlignCenter>
            {props.property.account.logo?.standard && (
              <img
                src={props.property.account.logo?.standard}
                alt="Developer Logo"
                style={{ marginRight: 15 }}
                data-testid="agent-logo-fixed"
              />
            )}

            <AgentText>Developed by {props.property.account.organisation}</AgentText>
          </RowAlignCenter>
        )}

        {!props.property.developerAsPrimaryBrand && (
          <>
            {props.sellingAgent && (
              <RowAlignCenter>
                {props.sellingAgent.logo && props.sellingAgent.logo.standard && (
                  <img
                    src={props.sellingAgent.logo.standard}
                    alt={`${props.property.development ? 'Developer' : 'Agent'} Logo`}
                    style={{ marginRight: 15 }}
                    data-testid="agent-logo-fixed"
                  />
                )}
                <NameAndPSRTextWrapper>
                  <AgentText>{props.sellingAgent.organisation}</AgentText>
                  {props.sellingAgent.psrLicenceNumber && props.property.countryCode === 'IRL' && (
                    <AgentText>
                      PSR Licence: {props.sellingAgent.psrLicenceNumber}
                      {props.sellingAgent.psrLicenceNumber === 'NO LICENCE' && (
                        <LicenceWarning href="/psr-licence-number" prefetch={false} style={{ textAlign: 'center' }}>
                          ?
                        </LicenceWarning>
                      )}
                    </AgentText>
                  )}
                </NameAndPSRTextWrapper>
              </RowAlignCenter>
            )}

            {!props.sellingAgent && (
              <RowAlignCenter>
                <AgentText>Marketed by multiple agents</AgentText>
              </RowAlignCenter>
            )}
          </>
        )}

        {props.property.published && (
          <AgentBoxFixedButtons numBtns={totalButtons}>
            {props.registerInterest && !props.leadsLink && (
              <Button
                containerStyle={{ height: 50 }}
                onClick={() => props.registerInterestClick('Sticky Bar')}
                testID="registerInterestFixed"
                type="button"
              >
                {useShortText ? 'Register' : 'Register Your Interest'}
              </Button>
            )}

            {props.leadsLink && (
              <Link href={props.leadsLink} style={{ width: '100%' }}>
                <Button
                  containerStyle={{ height: 50 }}
                  color={pdpBrandingButtonBgColour}
                  fontColor={pdpBrandingButtonTextColour}
                  type="button"
                >
                  {useShortText ? 'Message' : 'Send another message'}
                </Button>
              </Link>
            )}

            {props.contactInfo.email && !props.leadsLink && (
              <Button
                containerStyle={{ height: 50 }}
                onClick={() => props.enquireNowClick('Sticky Bar')}
                testID="enquireNowFixed"
                type="button"
                color={pdpBrandingButtonBgColour}
                fontColor={pdpBrandingButtonTextColour}
              >
                {useShortText ? 'Email' : platform.select('Email the Agent', 'Message Agent')}
              </Button>
            )}

            {props.contactInfo.phone && (
              <PropertyPhoneNumberButton
                {...props}
                containerStyle={{ height: 50 }}
                location="Sticky Bar"
                useShortText={useShortText}
              />
            )}

            {props.broker && platform.type === 'propertypal' && (
              <WhiteButton
                onClick={() => router.replace(`${props.property.path}/broker`, undefined, { shallow: true })}
              >
                {isMobile ? 'Mortgage Advice' : 'Request Mortgage Advice'}
              </WhiteButton>
            )}
          </AgentBoxFixedButtons>
        )}
      </AgentBoxFixed>

      <div ref={contactBox} />
    </>
  );
};

export default PropertyAgentBoxFixed;
